import constate from "constate";
import { useState } from "react";
import { GroupShiftInvite, Severity, ShiftInviteInfo } from "../ShiftInviteReview";
import { useShiftTimes } from "@src/containers/postShifts/hooks/useShiftTimes";
import { SessionType } from "@src/modules/interface";
import { useSelector } from "react-redux";
import { DEFAULT_TIMEZONE } from "@src/constants/timezone";
import { SelectedShiftInfo } from "../ShiftTypeTimeSelection";
import { isDefined } from "@clipboard-health/util-ts";
import { getDocsNames, getShiftInfoKey, generateDocumentListText, toShiftType } from "../utils";
import {
  moneyMajorUnitsToMinorUnits,
  canUserEditShiftRates,
} from "@src/appV2/FacilityDashboard/VariableCharge/utils";
import { useValidateShiftInvite } from "@src/appV2/ShiftInvites/api/useValidateShiftInvite";
import { AxiosError } from "axios";
import { NON_BOOKABLE_CRITERIA } from "../constants";
import { RequirementMappingLevel } from "@src/constants/documents";
import { HCF_USER_EVENTS } from "@src/constants/firebaseEvents";
import { logEvent } from "src/appV2/lib/analytics";
import { NearbyWorkerSearchResult } from "../Autocomplete/useGetNearbyWorkers";
import { zonedTimeToUtc } from "date-fns-tz";
import { differenceInMinutes, isValid } from "date-fns";
import { CbhFeatureFlag, useCbhFlag } from "@src/appV2/FeatureFlags";
import {
  useCreateShiftInvitePrice,
  type ChargeMember,
} from "@src/appV2/ShiftInvites/api/useCreateShiftInvitePrice";
import { useCheckShiftInviteDetail } from "@src/appV2/ShiftInvites/api/useCheckShiftInviteDetail";
import { type Money } from "@src/appV2/lib/Money/types";
import { showErrorToast } from "@src/appV2/lib/Notifications/Toasts/showToast";

const MIN_SHIFT_DURATION_MINUTES = 30;

interface UseShiftInviteParams {
  agentReq: string;
}

type MultipleShiftInfoKey = string;

interface ValidateShiftInfoParams {
  shiftInviteInfo: ShiftInviteInfo;
  selectedWorker: NearbyWorkerSearchResult | undefined;
  inFlightInvites?: Array<{
    start: Date;
    end: Date;
    agentReq: string;
    name: string;
    tmz: string;
  }>;
}

interface ValidateAndUpdateShiftInviteParams {
  selectedShiftInfo: SelectedShiftInfo;
  selectedDate: Date | undefined;
  selectedWorker: NearbyWorkerSearchResult | undefined;
}

interface ShiftInviteValidationItem {
  message: string;
  level: Severity;
  missingDocuments?: string[];
  isOverworkingShift?: boolean;
  tooltip?: string;
}

interface ShiftInviteValidationDetails {
  message: string;
  level: Severity;
  missingDocuments?: string[];
  isOverworkingShift?: boolean;
  tooltip?: string;
  items: ShiftInviteValidationItem[];
}

function getSeverityPriority(level: Severity): number {
  switch (level) {
    case Severity.ERROR:
      return 3;
    case Severity.WARN:
      return 2;
    case Severity.INFO:
      return 1;
    default:
      return 0;
  }
}

function aggregateValidationItems(
  items: ShiftInviteValidationItem[]
): ShiftInviteValidationDetails {
  const highestLevel = items.reduce<Severity>(
    (highest, item) =>
      getSeverityPriority(item.level) > getSeverityPriority(highest) ? item.level : highest,
    Severity.INFO
  );

  const allMissingDocuments = items.flatMap((item) => item.missingDocuments ?? []);
  const hasOverworkingShift = items.some((item) => item.isOverworkingShift);

  const primaryItem =
    items.length === 1
      ? items[0]
      : items.reduce((primary, item) =>
          getSeverityPriority(item.level) > getSeverityPriority(primary.level) ? item : primary
        );

  const combinedTooltip = items.map((item) => item.tooltip ?? item.message).join("\n\n");

  return {
    message: primaryItem.message,
    level: highestLevel,
    missingDocuments: allMissingDocuments.length > 0 ? allMissingDocuments : undefined,
    isOverworkingShift: hasOverworkingShift || undefined,
    tooltip: combinedTooltip,
    items,
  };
}

interface BaseUserJourneyEvent {
  recordedAt: Date;
  component: string;
}

const IMMEDIATE_EVENTS = ["open", "click", "select", "deselect", "start typing"] as const;
const DURATION_EVENTS = ["end typing", "submit", "exit"] as const;
// FIXME: Automatically disabled during ESLint config update
const USER_JOURNEY_EVENTS = [...IMMEDIATE_EVENTS, ...DURATION_EVENTS, "hover"] as const;
// eslint-disable-next-line @typescript-eslint/no-unused-vars
type ImmediateEventTypes = (typeof IMMEDIATE_EVENTS)[number];
type DurationEventTypes = (typeof DURATION_EVENTS)[number];
type UserJourneyEventTypes = (typeof USER_JOURNEY_EVENTS)[number];

interface ImmediateEvent extends BaseUserJourneyEvent {
  event: ImmediateEventTypes;
}

interface DurationEvent extends BaseUserJourneyEvent {
  event: DurationEventTypes;
  durationInMilliseconds: number;
  matchingEvent: number;
}

interface HoverEvent extends BaseUserJourneyEvent {
  event: "hover";
  durationInMilliseconds: number;
}

export type ShiftInviteUserJourneyEvent = ImmediateEvent | DurationEvent | HoverEvent;

interface LegacyChargeDetails {
  source: "legacy";
  charge: number;
  chargeBreakdown: {
    baseCharge: number;
    holiday: number;
    customRateBoost?: number;
  };
  name?: string | null;
  start: Date;
}

interface PriceApiChargeDetails {
  source: "price-api";
  charge: Money;
  chargeMembers?: ChargeMember[];
}

export type ChargeDetails = LegacyChargeDetails | PriceApiChargeDetails;

function useShiftInvite(params: UseShiftInviteParams) {
  const { agentReq } = params;
  const { profile } = useSelector((state: SessionType) => state.session);
  const workplaceId = profile?.userId;
  const facilityTimeZone = profile?.tmz ?? DEFAULT_TIMEZONE;
  const restrictOverworkShiftInvites = profile?.restrictOverworkShiftInvites ?? false;
  const { data: shiftTimes } = useShiftTimes({
    workplaceId,
    qualification: agentReq,
  });
  const [customChargeRateBoost, setCustomChargeRateBoost] = useState<number>();

  const isShiftInviteFacilityDocumentOverrideEnabled = useCbhFlag(
    CbhFeatureFlag.OVERRIDE_FACILITY_REQUIRED_DOCS_FOR_SHIFT_INVITE,
    { defaultValue: true }
  );
  const postShiftToMarketplaceDefaultValue = useCbhFlag(
    CbhFeatureFlag.SHIFT_INVITE_POST_TO_MARKETPLACE_DEFAULT,
    { defaultValue: true }
  );
  const [groupInviteSharedData, setGroupInviteSharedData] = useState<GroupShiftInvite>({
    workplaceId,
    agentReq,
    tmz: facilityTimeZone,
    postShiftToMarketplace: postShiftToMarketplaceDefaultValue,
  });
  const [multipleShiftInfo, setMultipleShiftInfo] = useState<
    Map<MultipleShiftInfoKey, ShiftInviteInfo>
  >(new Map<MultipleShiftInfoKey, ShiftInviteInfo>());
  const [multipleShiftValidationDetails, setMultipleShiftValidationDetails] = useState<
    Map<string, ShiftInviteValidationDetails>
  >(new Map<string, ShiftInviteValidationDetails>());
  const [multipleCheckShiftInviteDetails, setMultipleCheckShiftInviteDetails] = useState<
    Map<string, ChargeDetails>
  >(new Map<string, ChargeDetails>());
  const [chargeConstraints, setChargeConstraints] = useState<
    | {
        maximumChargeInMinorUnits: number;
        baseChargeInMinorUnits: number;
      }
    | undefined
  >();
  const [shiftInviteUserJourney, setShiftInviteUserJourney] = useState<
    ShiftInviteUserJourneyEvent[]
  >([]);
  const [userJourneyHoverStartDate, setUserJourneyHoverStartDate] = useState<Date | undefined>();
  const { mutateAsync: validateShiftInviteAsync, isLoading: isValidatingShiftInfo } =
    useValidateShiftInvite();
  const { mutateAsync: createShiftInvitePriceAsync } = useCreateShiftInvitePrice();
  const { mutateAsync: checkShiftInviteDetailAsync } = useCheckShiftInviteDetail();
  const isShiftInvitePriceApiEnabled = useCbhFlag(CbhFeatureFlag.SHIFT_INVITE_PRICE_API, {
    defaultValue: false,
  });
  const { user, admin } = useSelector((state: SessionType) => state.session);

  const hasChargeRatePermission = canUserEditShiftRates(user, admin);

  function getShiftInviteEventProperties(selectedWorker: NearbyWorkerSearchResult | undefined) {
    return {
      workplaceId,
      workerType: agentReq,
      ...(admin ? { adminUserId: user?._id } : { workplaceUserId: user?._id }),
      ...selectedWorker,
    };
  }

  async function validateShiftInfo(validateShiftInfoParams: ValidateShiftInfoParams) {
    const { shiftInviteInfo, selectedWorker, inFlightInvites } = validateShiftInfoParams;
    try {
      await validateShiftInviteAsync({
        shiftInviteRequest: {
          workerId: selectedWorker?.workerId ?? "",
          workplaceId,
          shiftDetails: {
            start: zonedTimeToUtc(shiftInviteInfo.start, facilityTimeZone),
            end: zonedTimeToUtc(shiftInviteInfo.end, facilityTimeZone),
            agentReq: groupInviteSharedData.agentReq,
          },
          postShiftToMarketplace: groupInviteSharedData.postShiftToMarketplace,
          inFlightInvites,
        },
      });
    } catch (error: unknown) {
      logEvent(HCF_USER_EVENTS.SHIFT_INVITE, {
        action: "VALIDATION_ERROR",
        ...getShiftInviteEventProperties(selectedWorker),
      });
      if (error instanceof AxiosError) {
        const errorData = error.response?.data.errors?.[0];
        const unmetCriteria: string[] = errorData?.reasons ?? [];
        const missingDocuments = errorData?.missingDocuments;
        const errorMessage = errorData?.detail ?? "Invalid Shift Detail";
        const items: ShiftInviteValidationItem[] = [];

        if (unmetCriteria.includes(NON_BOOKABLE_CRITERIA.WORKER_MISSING_REQUIREMENTS)) {
          const documentNames = getDocsNames(missingDocuments);
          if (!isShiftInviteFacilityDocumentOverrideEnabled) {
            items.push({
              message: "Worker is missing required documents",
              tooltip: generateDocumentListText({ documents: documentNames }),
              level: Severity.ERROR,
              missingDocuments: documentNames,
            });
          } else {
            const missingStateOrClipboardDocs = missingDocuments?.filter(
              (document: { level: string }) => document.level !== RequirementMappingLevel.HCF
            );
            if (missingStateOrClipboardDocs?.length > 0) {
              const stateOrClipboardDocumentNames = getDocsNames(missingStateOrClipboardDocs);
              items.push({
                message: "Worker is missing state or Clipboard requirements",
                tooltip: generateDocumentListText({
                  documents: stateOrClipboardDocumentNames,
                  requiredBy: "state or Clipboard Health",
                }),
                level: Severity.ERROR,
                missingDocuments: stateOrClipboardDocumentNames,
              });
            } else {
              items.push({
                message: "Worker is missing workplace requirements",
                tooltip: generateDocumentListText({
                  documents: documentNames,
                  requiredBy: "facility",
                }),
                level: Severity.WARN,
                missingDocuments: documentNames,
              });
            }
          }
        }

        if (unmetCriteria.includes(NON_BOOKABLE_CRITERIA.WORKER_HAS_REJECTED_BACKGROUND_CHECK)) {
          items.push({
            message: "The worker's background check was rejected",
            tooltip: "The worker's background check was rejected and they cannot book the shift",
            level: Severity.ERROR,
          });
        }

        if (unmetCriteria.includes(NON_BOOKABLE_CRITERIA.SHIFT_OVERWORKING)) {
          if (restrictOverworkShiftInvites) {
            items.push({
              message: "Overwork error",
              tooltip:
                "This invite cannot be sent because the worker would exceed the mandatory 17-hour limit",
              level: Severity.ERROR,
              isOverworkingShift: true,
            });
          } else {
            items.push({
              message: "Potential overwork",
              tooltip: "This shift may lead to work exceeding the recommended 17-hour limit",
              level: Severity.WARN,
              isOverworkingShift: true,
            });
          }
        }

        if (unmetCriteria.includes(NON_BOOKABLE_CRITERIA.SHIFT_OVERWORKING_FROM_INVITES)) {
          items.push({
            message: "Potential overwork from invites",
            tooltip:
              "If all pending invites are accepted, this shift may lead to work exceeding the recommended 17-hour limit",
            level: Severity.WARN,
            isOverworkingShift: true,
          });
        }

        if (
          unmetCriteria.includes(NON_BOOKABLE_CRITERIA.WORKER_IS_MISSING_DATA_FOR_BACKGROUND_CHECK)
        ) {
          items.push({
            message:
              "Professional needs to provide additional information for a background check after accepting invite",
            tooltip:
              "The professional's background check may not be valid for the shift. We will ask for additional information when they accept the invite.",
            level: Severity.WARN,
          });
        }

        if (unmetCriteria.includes(NON_BOOKABLE_CRITERIA.SHIFT_OVERLAPPING)) {
          items.push({
            message: "Overlapping shift",
            tooltip: "This worker already has a booked shift that overlaps with this time slot",
            level: Severity.WARN,
          });
        }

        if (items.length === 0) {
          items.push({ message: errorMessage, level: Severity.ERROR });
        }

        return aggregateValidationItems(items);
      }
    }
  }

  async function withShiftDetailErrorLogging<T>(
    fn: () => Promise<T>,
    selectedWorker: NearbyWorkerSearchResult | undefined
  ): Promise<T | undefined> {
    try {
      return await fn();
    } catch {
      logEvent(HCF_USER_EVENTS.SHIFT_INVITE, {
        action: "CHECK_SHIFT_DETAILS_ERROR",
        ...getShiftInviteEventProperties(selectedWorker),
      });
      return undefined;
    }
  }

  async function fetchShiftInvitePrice(
    shiftInviteInfo: ShiftInviteInfo,
    selectedWorker: NearbyWorkerSearchResult | undefined,
    customBoost?: number
  ) {
    if (!isDefined(selectedWorker)) {
      showErrorToast("No worker selected. Please select a worker and try again.");
      return undefined;
    }

    return withShiftDetailErrorLogging(async () => {
      const startAt = zonedTimeToUtc(shiftInviteInfo.start, facilityTimeZone).toISOString();
      const endAt = zonedTimeToUtc(shiftInviteInfo.end, facilityTimeZone).toISOString();
      const result = await createShiftInvitePriceAsync({
        qualification: groupInviteSharedData.agentReq,
        workerId: selectedWorker.workerId,
        startAt,
        endAt,
        workplaceId,
        shiftType: toShiftType(shiftInviteInfo.name),
        customRateBoost: isDefined(customBoost)
          ? {
              amountInMinorUnits: moneyMajorUnitsToMinorUnits(customBoost),
              currencyCode: "USD",
            }
          : undefined,
      });
      return result.data.attributes;
    }, selectedWorker);
  }

  async function checkShiftInviteDetail(checkShiftInviteDetailParams: ValidateShiftInfoParams) {
    const { shiftInviteInfo, selectedWorker } = checkShiftInviteDetailParams;

    if (!isDefined(selectedWorker)) {
      showErrorToast("No worker selected. Please select a worker and try again.");
      return undefined;
    }

    return withShiftDetailErrorLogging(
      () =>
        checkShiftInviteDetailAsync({
          shiftInviteRequest: {
            workerId: selectedWorker.workerId,
            workplaceId,
            shiftDetails: {
              start: zonedTimeToUtc(shiftInviteInfo.start, facilityTimeZone),
              end: zonedTimeToUtc(shiftInviteInfo.end, facilityTimeZone),
              agentReq: groupInviteSharedData.agentReq,
              customChargeRateBoost,
            },
            postShiftToMarketplace: groupInviteSharedData.postShiftToMarketplace,
          },
        }),
      selectedWorker
    );
  }

  const validateAndUpdateShiftInvite = async (
    validateAndUpdateShiftInviteParams: ValidateAndUpdateShiftInviteParams
  ) => {
    const { selectedShiftInfo, selectedDate, selectedWorker } = validateAndUpdateShiftInviteParams;
    const { shiftType, start, end, isSelected } = selectedShiftInfo;
    if (
      !isDefined(start) ||
      !isDefined(end) ||
      !isDefined(selectedDate) ||
      !isValid(start) ||
      !isValid(end) ||
      !isValid(selectedDate)
    ) {
      return;
    }
    const shiftInfoKey = getShiftInfoKey({
      date: selectedDate,
      name: shiftType,
    });
    const newShiftInfo = {
      start,
      end,
      name: shiftType,
      selectedDate,
      customChargeRateBoost,
    };

    // Capture the updated state for in-flight context calculation
    let updatedShiftsMap: Map<string, ShiftInviteInfo> = new Map();
    setMultipleShiftInfo((previousMultipleShiftInfo) => {
      const newMultipleShiftInfo = new Map(previousMultipleShiftInfo);
      if (isSelected) {
        newMultipleShiftInfo.set(shiftInfoKey, newShiftInfo);
      } else {
        newMultipleShiftInfo.delete(shiftInfoKey);
      }
      updatedShiftsMap = newMultipleShiftInfo;
      return newMultipleShiftInfo;
    });

    let validationDetails: ShiftInviteValidationDetails | undefined;

    if (isSelected) {
      if (isShiftInvitePriceApiEnabled && hasChargeRatePermission) {
        void fetchChargeConstraints(selectedWorker, newShiftInfo);
      }

      const durationInMinutes = differenceInMinutes(end, start);
      if (durationInMinutes < MIN_SHIFT_DURATION_MINUTES) {
        const item: ShiftInviteValidationItem = {
          message: `Shift duration must be at least ${MIN_SHIFT_DURATION_MINUTES} minutes`,
          level: Severity.ERROR,
        };
        validationDetails = { ...item, items: [item] };
      } else {
        // Get all OTHER selected shifts (excluding the current one) from the UPDATED state
        const otherInvites = Array.from(updatedShiftsMap.values())
          .filter(
            (shift) =>
              getShiftInfoKey({
                date: shift.selectedDate!,
                name: shift.name,
              }) !== shiftInfoKey
          )
          .sort((a, b) => a.start.getTime() - b.start.getTime())
          .map((shift) => ({
            start: zonedTimeToUtc(shift.start, facilityTimeZone),
            end: zonedTimeToUtc(shift.end, facilityTimeZone),
            agentReq: groupInviteSharedData.agentReq,
            name: shift.name,
            tmz: facilityTimeZone,
          }));

        validationDetails = await validateShiftInfo({
          shiftInviteInfo: newShiftInfo,
          selectedWorker,
          inFlightInvites: otherInvites.length > 0 ? otherInvites : undefined,
        });
      }
    }

    setMultipleShiftValidationDetails((previousMultipleShiftValidationDetails) => {
      const newMultipleShiftValidationDetails = new Map(previousMultipleShiftValidationDetails);
      if (isDefined(validationDetails)) {
        newMultipleShiftValidationDetails.set(shiftInfoKey, validationDetails);
      } else {
        newMultipleShiftValidationDetails.delete(shiftInfoKey);
      }
      return newMultipleShiftValidationDetails;
    });

    // When a shift is deselected, revalidate nearby shifts that might be affected
    // Two shifts can affect each other if they could be in the same 24-hour rolling window
    // Optimization: Only revalidate shifts that have validation issues, since deselection can only improve validation status
    if (!isSelected) {
      const TWENTY_FOUR_HOURS_IN_MS = 24 * 60 * 60 * 1000;
      const deselectedShiftStart = start.getTime();
      const deselectedShiftEnd = end.getTime();

      const shiftsToRevalidate = Array.from(updatedShiftsMap.entries())
        .filter(([shiftKey, shift]) => {
          // Check if shifts could be in the same 24-hour rolling window
          // This happens when the time from earliest start to latest end is <= 24 hours
          const earliestStart = Math.min(shift.start.getTime(), deselectedShiftStart);
          const latestEnd = Math.max(shift.end.getTime(), deselectedShiftEnd);
          const timeSpan = latestEnd - earliestStart;
          const hasValidationIssues = multipleShiftValidationDetails.has(shiftKey);
          // Only revalidate shifts that could be in same 24-hour window AND have validation issues
          return timeSpan <= TWENTY_FOUR_HOURS_IN_MS && hasValidationIssues;
        })
        .sort(([, a], [, b]) => a.start.getTime() - b.start.getTime());

      // Revalidate each affected shift sequentially with updated in-flight context
      // This includes shifts both before and after the deselected shift (within 17 hour window)
      for (const [revalidateKey, shiftToRevalidate] of shiftsToRevalidate) {
        const shiftsBeforeRevalidate = Array.from(updatedShiftsMap.values())
          .filter(
            (shift) =>
              shift.start.getTime() < shiftToRevalidate.start.getTime() &&
              getShiftInfoKey({
                date: shift.selectedDate!,
                name: shift.name,
              }) !== revalidateKey
          )
          .sort((a, b) => a.start.getTime() - b.start.getTime())
          .map((shift) => ({
            start: zonedTimeToUtc(shift.start, facilityTimeZone),
            end: zonedTimeToUtc(shift.end, facilityTimeZone),
            agentReq: groupInviteSharedData.agentReq,
            name: shift.name,
            tmz: facilityTimeZone,
          }));

        const revalidatedDetails = await validateShiftInfo({
          shiftInviteInfo: shiftToRevalidate,
          selectedWorker,
          inFlightInvites: shiftsBeforeRevalidate.length > 0 ? shiftsBeforeRevalidate : undefined,
        });

        // Update validation details for the revalidated shift
        setMultipleShiftValidationDetails((prev) => {
          const updated = new Map(prev);
          if (isDefined(revalidatedDetails)) {
            updated.set(revalidateKey, revalidatedDetails);
          } else {
            updated.delete(revalidateKey);
          }
          return updated;
        });
      }
    }
  };

  async function fetchChargeDetailsForSelectedShifts(
    selectedWorker: NearbyWorkerSearchResult | undefined
  ) {
    if (!isDefined(customChargeRateBoost)) {
      return;
    }

    const results = await Promise.all(
      Array.from(multipleShiftInfo.entries()).map(
        async ([shiftInfoKey, shiftInfo]): Promise<[string, ChargeDetails | undefined]> => {
          if (isShiftInvitePriceApiEnabled) {
            const priceAttributes = await fetchShiftInvitePrice(
              shiftInfo,
              selectedWorker,
              customChargeRateBoost
            );
            return [
              shiftInfoKey,
              isDefined(priceAttributes?.charge)
                ? {
                    source: "price-api",
                    charge: priceAttributes.charge,
                    chargeMembers: priceAttributes.chargeMembers,
                  }
                : undefined,
            ];
          }

          const response = await checkShiftInviteDetail({
            shiftInviteInfo: shiftInfo,
            selectedWorker,
          });
          const shiftDetails = response?.data.data.attributes.shiftDetails;
          return [
            shiftInfoKey,
            isDefined(shiftDetails)
              ? {
                  source: "legacy",
                  charge: shiftDetails.charge,
                  chargeBreakdown: shiftDetails.chargeBreakdown,
                  name: shiftDetails.name,
                  start: shiftDetails.start,
                }
              : undefined,
          ];
        }
      )
    );

    setMultipleCheckShiftInviteDetails((previous) => {
      const updated = new Map(previous);
      for (const [shiftInfoKey, chargeDetails] of results) {
        if (isDefined(chargeDetails)) {
          updated.set(shiftInfoKey, chargeDetails);
        } else {
          updated.delete(shiftInfoKey);
        }
      }
      return updated;
    });
  }

  async function fetchChargeConstraints(
    selectedWorker: NearbyWorkerSearchResult | undefined,
    shiftInfo: ShiftInviteInfo
  ) {
    const priceAttributes = await fetchShiftInvitePrice(shiftInfo, selectedWorker);
    const chargeRange = priceAttributes?.constraints?.charge;
    if (isDefined(chargeRange) && isDefined(priceAttributes?.charge)) {
      setChargeConstraints({
        maximumChargeInMinorUnits: chargeRange.maximumAmount.amountInMinorUnits,
        baseChargeInMinorUnits: priceAttributes.charge.amountInMinorUnits,
      });
    } else {
      setChargeConstraints(undefined);
    }
  }

  function addUserJourneyEvent(eventType: UserJourneyEventTypes, component: string) {
    const recordedAt = new Date();
    const newEvent = {
      event: eventType,
      component,
      recordedAt,
    };
    if (DURATION_EVENTS.includes(eventType as DurationEventTypes)) {
      const { durationInMilliseconds, matchingEventIndex } = getDurationSinceMatchingEvent(
        eventType as DurationEventTypes,
        recordedAt
      );
      if (isDefined(durationInMilliseconds)) {
        setShiftInviteUserJourney((previousShiftInviteUserJourney) => {
          return [
            ...previousShiftInviteUserJourney,
            {
              ...newEvent,
              event: eventType as DurationEventTypes,
              durationInMilliseconds,
              matchingEvent: matchingEventIndex,
            },
          ];
        });

        return;
      }
    }
    // Since the event is a duration event, it should always find a matching event.
    // However if not, we default to logging as an immediate event

    setShiftInviteUserJourney((previousShiftInviteUserJourney) => {
      return [...previousShiftInviteUserJourney, newEvent as ImmediateEvent];
    });
  }

  function trackUserJourneyHoverStart() {
    setUserJourneyHoverStartDate(new Date());
  }

  function addUserJourneyHoverEndEvent(component: string) {
    const HOVER_DURATION_THRESHOLD_IN_MILLISECONDS = 1000;
    if (isDefined(userJourneyHoverStartDate)) {
      const durationInMilliseconds = Date.now() - userJourneyHoverStartDate.getTime();
      if (durationInMilliseconds >= HOVER_DURATION_THRESHOLD_IN_MILLISECONDS) {
        setShiftInviteUserJourney((previousShiftInviteUserJourney) => {
          return [
            ...previousShiftInviteUserJourney,
            {
              event: "hover" as const,
              component,
              recordedAt: userJourneyHoverStartDate,
              durationInMilliseconds,
            },
          ];
        });
      }
      setUserJourneyHoverStartDate(undefined);
    }
  }

  function getDurationSinceMatchingEvent(
    eventType: DurationEventTypes,
    recordedAt: Date
  ): {
    durationInMilliseconds: number | undefined;
    matchingEventIndex: number;
  } {
    const matchingEventMap = {
      submit: "open",
      exit: "open",
      "end typing": "start typing",
    };
    const matchingEventIndex = shiftInviteUserJourney.findLastIndex(
      (event) => event.event === matchingEventMap[eventType]
    );
    if (matchingEventIndex !== -1) {
      return {
        durationInMilliseconds:
          recordedAt.getTime() - shiftInviteUserJourney[matchingEventIndex].recordedAt.getTime(),
        matchingEventIndex,
      };
    }

    return { durationInMilliseconds: undefined, matchingEventIndex: -1 };
  }

  function logUserJourney(endEventType: "exit" | "submit", component: string) {
    const recordedAt = new Date();
    const { durationInMilliseconds, matchingEventIndex } = getDurationSinceMatchingEvent(
      endEventType,
      recordedAt
    );
    const events = [
      ...shiftInviteUserJourney,
      {
        event: endEventType,
        component,
        recordedAt,
        durationInMilliseconds,
        matchingEvent: matchingEventIndex,
      },
    ];
    logEvent(HCF_USER_EVENTS.SHIFT_INVITE_USER_JOURNEY, {
      action: endEventType,
      events,
      workplaceId,
      ...(admin ? { adminUserId: user?._id } : { workplaceUserId: user?._id }),
    });
  }

  return {
    groupInviteSharedData,
    setGroupInviteSharedData,
    multipleShiftInfo,
    setMultipleShiftInfo,
    multipleShiftValidationDetails,
    setMultipleShiftValidationDetails,
    shiftTimes,
    validateAndUpdateShiftInvite,
    isValidatingShiftInfo,
    facilityTimeZone,
    customChargeRateBoost,
    setCustomChargeRateBoost,
    multipleCheckShiftInviteDetails,
    setMultipleCheckShiftInviteDetails,
    fetchChargeDetailsForSelectedShifts,
    chargeConstraints,
    shiftInviteUserJourney,
    addUserJourneyEvent,
    trackUserJourneyHoverStart,
    addUserJourneyHoverEndEvent,
    logUserJourney,
    isShiftInvitePriceApiEnabled,
  };
}

export const [ShiftInviteProvider, useShiftInviteContext] = constate(useShiftInvite);
